/** @jsx jsx */
import { List } from "@cd2/components"
import { jsx } from "@emotion/core"
import React from "react"
import { LabelInfo } from "../Components/LabelInfo"
import { buildRoute, eRoutes } from "../routes"
import { IRecord } from "../types/schema"
import { HoverCustomerLoader } from "./HoverCustomerLoader"
import { HoverResourceLoader } from "./HoverResourceLoader"
import { HoverUserLoader } from "./HoverUserLoader"
import { titleize } from "@cd2/tools"
import { HoverOrderLoader } from "./HoverOrderLoader"
import { HoverProductLoader } from "./HoverProductLoader"

type ISupportTicket = IRecord<
  "support_tickets",
  | "title"
  | "assigned_to_user"
  | "customer"
  | "customerName"
  | "identifier"
  | "status"
  | "order"
  | "product"
  | "priority"
> & {
  contact?: IRecord<"contacts", "name" | "email">
}

interface IProps {
  id: string
  initialData?: ISupportTicket
  renderInlineLink?: "block" | "inline"
  noLink?: boolean
  inline?: boolean
  resourceName?: string
}

export function HoverSupportTicketLoader({
  id,
  initialData,
  renderInlineLink,
  noLink,
  inline,
  resourceName,
}: IProps) {
  const skeem = window.skeem

  async function loadData() {
    const user = await skeem.fetchOne<ISupportTicket>({
      support_tickets: {
        attributes: [
          "title",
          "assigned_to_user",
          "customer",
          "customerName",
          "identifier",
          "status",
          "order",
          "product",
          "priority",
          { name: "contact", attributes: ["name", "email"] },
        ],
        filter: { matches: { id } },
      },
    })
    return user
  }

  return (
    <HoverResourceLoader
      reloadKey={id}
      loadData={loadData}
      initialData={initialData}
      viewLink={ticket =>
        buildRoute(eRoutes["/customer-support/support-tickets/:id"], { id: ticket.id })
      }
      renderInlineLink={renderInlineLink}
      resourceName={resourceName ?? "Support Ticket"}
      renderHoverContentTitle={record => record.identifier}
      renderHoverContent={record => (
        <div>
          <List>
            <LabelInfo labelWidth={80} label="Status" value={titleize(record.status)} inline />
            <LabelInfo labelWidth={80} label="Priority" value={titleize(record.priority)} inline />
            <LabelInfo labelWidth={80} label="Identifier" value={record.identifier} inline />
            <LabelInfo labelWidth={80} label="Title" value={record.title} inline />
            {record.customer ? (
              <LabelInfo
                labelWidth={80}
                label="Customer"
                value={<HoverCustomerLoader id={record.customer.id} />}
                inline
              />
            ) : (
              <LabelInfo labelWidth={80} label="Customer" value={record.customerName} inline />
            )}
            {record.contact && (
              <React.Fragment>
                <LabelInfo
                  labelWidth={80}
                  label="Contact Name"
                  value={record.contact.name}
                  inline
                />
                <LabelInfo
                  labelWidth={80}
                  label="Contact Email"
                  value={record.contact.email}
                  inline
                />
              </React.Fragment>
            )}
            {record.assigned_to_user && (
              <LabelInfo
                labelWidth={80}
                label="Assigned To"
                value={
                  <HoverUserLoader id={record.assigned_to_user.id} resourceName="Assigned To" />
                }
                inline
              />
            )}
            {record.order && (
              <LabelInfo
                labelWidth={80}
                label="Order"
                value={<HoverOrderLoader id={record.order.id} />}
                inline
              />
            )}
            {record.product && (
              <LabelInfo
                labelWidth={80}
                label="Product"
                value={<HoverProductLoader id={record.product.id} />}
                inline
              />
            )}
          </List>
        </div>
      )}
      renderTarget={record => <span>{record.identifier}</span>}
      inline={inline}
      noLink={noLink}
    />
  )
}
